import { useEffect } from 'react';
import * as Styles from './TechnicalValidationWelcoming.styles';

const DEFAULT_LINKS = {
  figma: {
    default:
      'https://www.figma.com/file/sbKqCqrV5UKfqK0pZo7RhA/Trio-Challenge-%7C-Bike-Rental-Website?node-id=102%3A557',
    reactNative:
      'https://www.figma.com/proto/sbKqCqrV5UKfqK0pZo7RhA/Trio-Challenge-%7C-Bike-Rental-Website?page-id=189%3A3639&node-id=555-6598&viewport=767%2C502%2C0.19&scaling=min-zoom&starting-point-node-id=555%3A7664',
  },
  flutter:
    'https://docs.google.com/document/d/1sxP91vZiY7McmSMmzrboklIJWQ_6pSS9MBSCrQq016M/edit?pli=1#',
  termoo:
    'https://seed-lantern-451.notion.site/Term-ooo-Discovering-word-of-the-day-ee437786e6584abf835f4295090769a9',
  completedApi: 'https://trio.notion.site/Trio-BikeRent-API-4c351f11728a4438a8a3d449e9f9e153',
  notCompletedApi: 'https://trio.notion.site/Trio-BikeRent-API-41a10a024dad4387877b1f0b5f8eb425',
};

const technologies = {
  backend: [
    'NodeJS',
    'Django',
    'Flask',
    'FastAPI',
    'Golang',
    'Java',
    'JavaEE',
    'Kotlin',
    'Rust',
    'Scala',
  ],
  frontend: ['ReactJS', 'VueJS', 'Angular'],
  mobile: ['Flutter', 'Objective C', 'React Native', 'Swift', 'Kotlin', 'Java'],
};

type TechnicalValidationWelcomingProps = {
  description?: string;
  name?: string;
};

export default function TechnicalValidationWelcoming({
  description,
  name,
}: TechnicalValidationWelcomingProps): JSX.Element {
  const doesNameIncludeTech = (tech: string): boolean => !!name?.includes(tech);

  const isPartnerTechnicalChallenge = name?.toLowerCase().includes('partner');
  const isBikeRentChallenge = name?.toLowerCase().includes('bikerent');
  const isGenericProject = !isBikeRentChallenge;
  const isReactNativeChallenge = name?.toLowerCase().includes('react native');
  // const frontEndStacks = [...technologies.frontend, ...technologies.mobile];
  const hasBackEndStack = technologies.backend.some(doesNameIncludeTech);
  // const hasFrontEndStack = frontEndStacks.some(doesNameIncludeTech);

  useEffect(() => {
    if (description) {
      const contentDivElement = document.getElementById('description-content');

      if (contentDivElement) {
        contentDivElement.innerHTML = description;
      }
    }

    const allLinks = document.querySelectorAll('a');
    allLinks.forEach((link) => {
      link.setAttribute('target', '_blank');
    });
  }, [description]);

  return (
    <div>
      <section>
        <Styles.Container>
          {!isPartnerTechnicalChallenge && !isGenericProject && (
            <div>
              <>
                <Styles.Title>Project Overview</Styles.Title>
                <Styles.RegularText marginBottom={16}>
                  Welcome to the technical portion of our application process! We’re excited that
                  you made it to this point and hope that you enjoy working on the upcoming Trio
                  Challenge, a special take-home project that we designed to asses your technical
                  maturity.
                </Styles.RegularText>
                <Styles.RegularText>
                  The Trio Challenge consists of a semi-built bike rental application. You will
                  receive a boilerplate and extend it with a new feature that allows users to rent a
                  bike!
                </Styles.RegularText>
              </>

              <>
                <Styles.Title>Deliverables</Styles.Title>
                <Styles.RegularText>
                  We’re looking to have a GitHub private repository with an application using the
                  provided boilerplate.
                </Styles.RegularText>
                <Styles.Subtitle>Deliverables</Styles.Subtitle>
                <Styles.List marginBottom={16}>
                  <Styles.ListItem>
                    Create a new empty private GitHub repository, then import one of the
                    boilerplates
                  </Styles.ListItem>
                  <Styles.ListItem>
                    Develop and deliver a solution that is production ready by opening a new PR on
                    your repository
                  </Styles.ListItem>
                  <Styles.ListItem>
                    Add <Styles.Highlight>challenge@trio.dev</Styles.Highlight> as a collaborator
                  </Styles.ListItem>
                  <Styles.ListItem>
                    Create a <Styles.Highlight>Technical Design</Styles.Highlight> for your
                    approach. Make sure to include an analysis of the project structure and share
                    your thoughts.
                  </Styles.ListItem>
                  <Styles.ListItem>
                    Record a video with your screen and camera walking through your solution
                  </Styles.ListItem>
                  <Styles.ListItem>
                    Submit your project by answering all fields on{' '}
                    <Styles.Highlight>the next page</Styles.Highlight>
                  </Styles.ListItem>
                </Styles.List>
                <Styles.RegularText>
                  You will have 5 days to complete the project and return it. If you have any
                  questions or are not sure about something, please contact challenge@trio.dev.
                </Styles.RegularText>
              </>
            </div>
          )}

          {/* Dynamic content will be inserted here */}
          <Styles.GenericContent
            withoutTopSpacing={isPartnerTechnicalChallenge || isGenericProject}
            id="description-content"
          />

          {isBikeRentChallenge && !hasBackEndStack && !isGenericProject && (
            <>
              <Styles.Subtitle>Trio BikeRent API</Styles.Subtitle>
              <Styles.RegularText>
                Trio BikeRent API supports multi-tenancy, where each candidate is considered a
                single tenant. To use this API you must create your candidate and user records
                following the Candidate creation and User creation sections from the API
                documentation. Put your candidate and user tokens inside of environment variables.
                To understand more, please read the{' '}
                <a
                  target="_blank"
                  href={
                    hasBackEndStack ? DEFAULT_LINKS.notCompletedApi : DEFAULT_LINKS.completedApi
                  }
                  rel="noreferrer"
                >
                  Trio BikeRent API documentation.
                </a>
              </Styles.RegularText>
            </>
          )}

          {!isPartnerTechnicalChallenge && !isGenericProject && (
            <>
              <Styles.Subtitle>Figma Design</Styles.Subtitle>
              <Styles.List marginBottom={32}>
                <Styles.AnchorListItem>
                  <a
                    target="_blank"
                    href={
                      isReactNativeChallenge
                        ? DEFAULT_LINKS.figma.reactNative
                        : DEFAULT_LINKS.figma.default
                    }
                    rel="noreferrer"
                  >
                    BikeRent Figma
                  </a>
                </Styles.AnchorListItem>
              </Styles.List>
            </>
          )}

          <>
            <Styles.Subtitle>Technical Design</Styles.Subtitle>
            <Styles.RegularText marginBottom={16}>
              Technical Designs are documents that structure the solution before its implementation.
              It should be simple and should not contain code, it only presents a structured idea to
              solve the given problem.
            </Styles.RegularText>
            <Styles.RegularText marginBottom={16}>
              Technical Design documents may compare different approaches and their trade-offs
              whenever the Software Engineer faces a dilemma in order to identify the best decision.
            </Styles.RegularText>
            <Styles.RegularText marginBottom={16}>
              Again be sure to include an analysis of the project structure and share your thoughts
              and suggestions.
            </Styles.RegularText>
            <Styles.RegularText marginBottom={16}>Examples</Styles.RegularText>
            <Styles.List marginBottom={0}>
              <Styles.AnchorListItem>
                <a target="_blank" href={DEFAULT_LINKS.flutter} rel="noreferrer">
                  Flutter
                </a>
              </Styles.AnchorListItem>
              <Styles.AnchorListItem>
                <a target="_blank" href={DEFAULT_LINKS.termoo} rel="noreferrer">
                  Term.ooo
                </a>
              </Styles.AnchorListItem>
            </Styles.List>
          </>
        </Styles.Container>
      </section>
    </div>
  );
}
